/* Cali Raised LED cart drawer - 2026-08-06. Paired with crled-cart-drawer.css/.liquid
 *
 * ADD TO CART -> intercepted, posted to /cart/add.js, drawer opens.
 * BUY NOW     -> intercepted too, but after the add we redirect to /checkout.
 *
 * Keeps the header cart count live without editing sections/header.liquid.
 * Keeps Cart.js in sync via CartJS.getCart().
 */
(function () {
  'use strict';

  var drawer, overlay, body, foot, subEl, hCount, busy = false;
  var CART_LINK = 'a[href$="/cart"], a[href$="/cart/"]';
  // The theme's own counters: .cart-icon-top in the header, .cart-count on the cart page.
  var THEME_COUNT = '.cart-icon-top, .cart-count, [class*="cart-count"], [class*="cart_count"]';
  // Names a compare_at_price markdown in the summary ("Labor Day Sale  -$50.00").
  // Overwritten from the drawer's [data-cd-sale-label] at init so the label changes
  // per promotion without a code edit. See snippets/crled-cart-drawer.liquid.
  var SALE_LABEL = 'Sale';

  function money(c) { return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Redo's "Free Returns + Package Protection" line.
   *
   * Identified by Redo's OWN product fields, never a hard-coded variant id: the coverage
   * product carries 96 variants (one per price tier - 3% of cart, min $1 / max $55, plus
   * $2.98 flat return coverage) and Redo picks one per cart, but they all share
   * sku "x-redo", vendor "re:do" and the one handle.
   *
   * It is a real Shopify line item that Redo adds on PAGE LOAD (not at add-to-cart), so
   * it can be absent from a drawer opened straight after ADD TO CART and appear later.
   *
   * We render it WITHOUT a quantity stepper - "package protection x3" is meaningless and
   * was previously reachable - and with a labelled Remove instead. Removal via
   * /cart/change.js sticks across reloads; Redo does not force it back.
   *
   * When Redo's own toggle starts injecting at [data-crled-redo] (blocked until Redo
   * removes us from their coverage experiment), switch this row to hidden and let the
   * toggle represent it - otherwise there will be two competing controls. */
  function isCoverage(i) {
    return i.sku === 'x-redo' || i.vendor === 're:do' ||
      i.handle === 'free-unlimited-return-valid-in-us';
  }

  /* Count of what the shopper actually put in the cart - coverage excluded.
   *
   * Every badge and count is driven from THIS number, not cart.item_count, for three
   * reasons discovered the hard way (2026-08-11):
   *   1. RACE-IMMUNITY. Redo re-tiers its coverage line (remove old variant, add new)
   *      at unpredictable times after any cart mutation. item_count flaps 4->3->4/5
   *      during the swap, and no fixed-delay re-read reliably lands after it - a
   *      settled read still showed 4 while /cart.js later said 5. The REAL-item count
   *      is identical on both sides of a swap, so the badge simply cannot flap.
   *   2. CONSISTENCY. Redo already rewrites counters matching [class*="cart-count"]
   *      site-wide with its own coverage-excluding number (measured: it stomped our
   *      setCount value and even a test value within a tick). Counting the same way
   *      means our surfaces and theirs agree instead of disagreeing by one.
   *   3. SHOPPER MENTAL MODEL. Someone who added 3 products expects "3".
   * snippets/crled-cart-badge-patch.liquid applies the same rule to the label-shaped
   * "Cart (N)" counters, and snippets/top-site-nav.liquid + snippets/mobile-side.liquid
   * server-render the same number - change them together or surfaces disagree. */
  function realCount(cart) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (i) { if (!isCoverage(i)) n += i.quantity; });
    return n;
  }

  function open() {
    if (!drawer) return;
    overlay.hidden = false;
    // Force reflow so the transition runs. NOT requestAnimationFrame: rAF is throttled
    // or never fires when the page is not actively rendering.
    void overlay.offsetWidth;
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('crled-cd-open');
  }

  function close() {
    if (!drawer) return;
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('crled-cd-open');
    setTimeout(function () { overlay.hidden = true; }, 300);
  }

  /* Give every cart link a LIVE count without adding a second number.
   *
   * The theme already prints <span class="cart-icon-top"> in the header, but it is
   * server-rendered ONLY - verified it does not update on an AJAX cart change, not even
   * after CartJS.getCart(). That was fine before this drawer existed, because adding to
   * cart navigated to /cart and re-rendered the whole page. Now that we keep the shopper
   * in place it goes stale, which is the entire reason a live count is needed here.
   *
   * Creating our own unconditionally put TWO numbers in the header, and they disagreed:
   * the theme's "3" (server-rendered, counting Redo's coverage line) beside our "2"
   * (fetched before Redo had added it). So ADOPT the theme's element where one exists and
   * drive that; only create a badge on links that have no counter of their own (e.g. the
   * mobile menu link, which renders just an icon and the word "Cart").
   *
   * The mobile header icon's bubble (snippets/mobile-side.liquid) is pre-tagged with
   * data-cd-count server-side and its class avoids the "cart-count" substring - see the
   * comment there for why. mountBadges leaves it alone via the first guard.
   *
   * Do not add .crled-cd-count to an adopted element - it should keep the theme's own
   * styling and position. */
  function mountBadges() {
    document.querySelectorAll(CART_LINK).forEach(function (a) {
      if (a.querySelector('[data-cd-count]')) return;
      var existing = a.querySelector(THEME_COUNT);
      if (existing) { existing.setAttribute('data-cd-count', ''); return; }
      if (getComputedStyle(a).position === 'static') a.style.position = 'relative';
      var b = document.createElement('span');
      b.className = 'crled-cd-count';
      b.setAttribute('data-cd-count', '');
      b.hidden = true;
      a.appendChild(b);
    });
  }

  /* ---------------------------------------------------------------------------------
   * /cart.js DISCOUNT FIELDS - read this before touching the maths below.
   *
   * For a CART-LEVEL discount (a code like "10off!", an automatic discount) Shopify does
   * NOT reduce final_line_price. Measured on an $849.99 bed rack with 10off! applied:
   *
   *     original_line_price 84999   final_line_price 84999   <- unchanged!
   *     line_price          76499   total_discount    8500
   *     line_level_discount_allocations  []          <- empty, it is cart-level
   *
   * So `original_line_price > final_line_price` is ALWAYS FALSE for cart-level discounts
   * and is the wrong test. `total_discount` is the reliable per-line signal, populated
   * for both cart-level and line-level discounts.
   * --------------------------------------------------------------------------------- */

  /* Itemised order summary from /cart.js - covers codes, automatic discounts and
   * app/Function discounts identically. The footer used to print total_price alone, so a
   * saved 10% code made an $849.99 bed rack read $765.00 with no explanation. */
  function summary(cart) {
    var origEl = drawer.querySelector('[data-cd-orig]');
    var rowsEl = drawer.querySelector('[data-cd-saverows]');
    var orig = cart.original_total_price != null ? cart.original_total_price : cart.total_price;
    // Markdown savings are invisible to Shopify, so the MSRP subtotal has to be
    // reconstructed. Without this the subtotal and the total print the same number
    // and the summary reads as full price on a cart that is genuinely discounted.
    var saleTotal = ((cart.items) || []).reduce(function (s, i) { return s + saleOffFor(i); }, 0);

    if (origEl) origEl.textContent = money(orig + saleTotal);
    if (subEl) subEl.textContent = money(cart.total_price);

    var noCode = drawer.querySelector('[data-cd-nocode]');
    if (noCode) noCode.hidden = !saleTotal;

    if (!rowsEl) return;

    var byName = {}, order = [];
    function bump(t, amt) {
      t = t || 'Discount';
      if (byName[t] == null) { byName[t] = 0; order.push(t); }
      byName[t] += amt || 0;
    }
    (cart.items || []).forEach(function (i) {
      (i.line_level_discount_allocations || []).forEach(function (a) {
        bump(a.discount_application && a.discount_application.title, a.amount);
      });
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      bump(d.title, d.total_allocated_amount);
    });

    var total = cart.total_discount || 0;
    if (!total && !saleTotal) { rowsEl.innerHTML = ''; rowsEl.hidden = true; return; }

    function row(name, amt) {
      return '<div class="crled-cd__save"><span class="crled-cd__save-name">' + esc(name) +
        '</span><span class="crled-cd__save-amt">&minus;' + money(amt) + '</span></div>';
    }

    var html = '';
    // Markdown row first: it is the headline offer, and it is the one the shopper was
    // promised on the PDP.
    if (saleTotal) html += row(SALE_LABEL, saleTotal);

    // Shopify-side discounts, reconciled against Shopify's own total ONLY. saleTotal is
    // deliberately excluded from this check - it is not part of cart.total_discount, and
    // folding it in would break the reconciliation the original author put here.
    if (total) {
      var named = order.reduce(function (s, t) { return s + byName[t]; }, 0);
      // If named rows do not reconcile with Shopify's total, show one honest "Savings"
      // line rather than a breakdown that does not add up.
      if (order.length && named === total) {
        html += order.map(function (t) { return row(t, byName[t]); }).join('');
      } else {
        html += row('Savings', total);
      }
    }

    rowsEl.innerHTML = html;
    rowsEl.hidden = false;
  }

  /* --- compare_at_price lookup (2026-08-31) ----------------------------------------
   * A price-markdown sale (variant price cut, compare_at_price left at MSRP) produces
   * NO Shopify discount object: total_discount, line_level_discount_allocations and
   * cart_level_discount_applications are all empty. The savings UI above was built for
   * discount CODES and therefore stayed dark on a genuine 10%-off sale - the drawer
   * showed a bare $449.99 on an item the PDP had just shown as $449.99 / was $499.99.
   *
   * /cart.js does NOT return compare_at_price (verified 2026-08-31, all 38 line keys).
   * It does return handle and variant_id, and /products/{handle}.js returns
   * variants[].compare_at_price in CENTS - same unit as /cart.js, so money() applies.
   *
   * Cache the PROMISE, not the result, so concurrent renders share one request. Delete
   * the entry on rejection so a transient failure can retry on the next render rather
   * than disabling savings for the whole session. */
  var compareCache = {};   // handle -> Promise<{ variantId: compareAtCents }>

  function fetchCompare(handle) {
    if (compareCache[handle]) return compareCache[handle];
    var p = fetch('/products/' + encodeURIComponent(handle) + '.js',
        { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (prod) {
        var map = {};
        ((prod && prod.variants) || []).forEach(function (v) {
          if (v.compare_at_price && v.compare_at_price > v.price) map[v.id] = v.compare_at_price;
        });
        return map;
      })
      .catch(function () { delete compareCache[handle]; return {}; });
    compareCache[handle] = p;
    return p;
  }

  /* Markdown saving for one line, in cents. 0 when unknown, not on sale, or coverage.
   * Reads the map stashed on the cached promise so render() can stay synchronous. */
  function saleOffFor(i) {
    if (isCoverage(i)) return 0;
    var c = compareCache[i.handle] && compareCache[i.handle].__map;
    if (!c) return 0;
    var was = c[i.variant_id];
    if (!was || was <= i.price) return 0;
    return (was - i.price) * i.quantity;
  }

  /* Warm the cache for every non-coverage line. Resolves true only if something NEW
   * landed, so refresh() can avoid a pointless second render.
   * MUST be called from refresh() only - never from render(), or the two recurse. */
  function ensureCompare(cart) {
    var handles = [];
    ((cart && cart.items) || []).forEach(function (i) {
      if (!isCoverage(i) && i.handle && handles.indexOf(i.handle) === -1) handles.push(i.handle);
    });
    var fresh = handles.filter(function (h) {
      return !(compareCache[h] && compareCache[h].__map);
    });
    if (!fresh.length) return Promise.resolve(false);
    return Promise.all(fresh.map(function (h) {
      return fetchCompare(h).then(function (map) {
        if (compareCache[h]) compareCache[h].__map = map;   // sync accessor for render()
      });
    })).then(function () { return true; });
  }

  /* renderToken guards render()'s async repaint - see refresh(). It arrived with the
   * "Customers Also Love" upsell row and OUTLIVED it: the compare_at_price repaint added
   * 2026-08-31 depends on it. Do not delete it.
   *
   * 2026-08-31 (Claude): the "Customers Also Love" upsell row was REMOVED, at Jake's
   * request. It ran from 2026-08-11 with no dataLayer event of any kind, so twenty days
   * of it produced no data and none can be recovered; and Shopify's `related` intent was
   * returning wrong-fitment substitutes (a 2016-2023 Tacoma bumper offered against the
   * 2024+ bumper) plus multi-variant "Options" links that navigated the shopper OUT of an
   * open cart with a CHECKOUT button in it.
   *
   * If an upsell returns here: quick-add only, no links out of the drawer, and ship it
   * with tracking on day one. */
  var renderToken = 0;

  function render(cart) {
    if (!body) return;
    renderToken++;

    if (!cart || !cart.item_count) {
      body.innerHTML = '<p class="crled-cd__empty">Your cart is empty.</p>';
      if (foot) foot.hidden = true;
      setCount(0);
      return;
    }

    body.innerHTML = cart.items.map(function (i, idx) {
      var line = idx + 1;
      var cov = isCoverage(i);
      var variant = (i.variant_title && i.variant_title !== 'Default Title' && !cov)
        ? '<p class="crled-cd__var">' + esc(i.variant_title) + '</p>' : '';
      // loading="eager": the drawer sits at translateX(100%), outside the viewport, so
      // lazy images would not load until it slides in - a visible blank flash.
      var img = i.image
        ? '<img class="crled-cd__img" src="' + esc(i.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')) +
          '" alt="' + esc(i.product_title) + '" loading="eager" decoding="async" width="76" height="76">'
        : '<div class="crled-cd__img"></div>';

      // See the field notes above: total_discount, not final_line_price.
      // Two independent savings can apply to one line and they stack:
      //   codeOff  - Shopify discount (code / automatic / Function). In total_discount.
      //   saleOff  - compare_at_price markdown. Invisible to Shopify; see saleOffFor().
      // base is the marked-down price x qty, i.e. pre-code but POST-markdown, so the
      // MSRP the shopper should see struck through is base + saleOff.
      var codeOff = i.total_discount || 0;
      var saleOff = saleOffFor(i);
      var base    = (i.original_line_price != null) ? i.original_line_price : i.line_price;
      var wasTot  = base + saleOff;
      var nowTot  = base - codeOff;          // equals i.line_price
      var price = (codeOff + saleOff) > 0
        ? '<span class="crled-cd__price is-disc"><span class="crled-cd__was">' +
          money(wasTot) + '</span>' + money(nowTot) + '</span>'
        : '<span class="crled-cd__price">' + money(i.line_price) + '</span>';

      // Only tag genuine LINE-level promos. A cart-level code is already named once in
      // the summary; repeating it on every row is noise.
      var tags = (i.line_level_discount_allocations || []).map(function (a) {
        var t = (a.discount_application && a.discount_application.title) || 'Discount';
        return '<span class="crled-cd__dtag">' + esc(t) + ' &minus;' + money(a.amount) + '</span>';
      }).join('');

      // Coverage gets a labelled Remove where the stepper would be, and no corner x -
      // one obvious control instead of two, and no meaningless quantity.
      var ctl = cov
        ? '<button type="button" class="crled-cd__covrm" data-cd-rm="' + line + '">Remove</button>'
        : '<span class="crled-cd__qty">' +
            '<button type="button" data-cd-dec="' + line + '" aria-label="Decrease quantity">-</button>' +
            '<span data-cd-qv>' + i.quantity + '</span>' +
            '<button type="button" data-cd-inc="' + line + '" aria-label="Increase quantity">+</button>' +
          '</span>';
      var corner = cov ? ''
        : '<button type="button" class="crled-cd__rm" data-cd-rm="' + line + '" aria-label="Remove item">&times;</button>';
      var name = cov
        ? '<span class="crled-cd__name">' + esc(i.product_title) + '</span>' +
          '<p class="crled-cd__var">Optional &middot; added automatically</p>'
        : '<a class="crled-cd__name" href="' + esc(i.url) + '">' + esc(i.product_title) + '</a>';

      return '<div class="crled-cd__item' + (cov ? ' crled-cd__item--cov' : '') + '">' + img +
        '<div class="crled-cd__meta">' + name + variant +
          '<div class="crled-cd__row">' + ctl + price + '</div>' + tags +
        '</div>' + corner +
        '</div>';
    }).join('');

    if (foot) foot.hidden = false;
    summary(cart);
    setCount(realCount(cart));
  }

  function setCount(n) {
    if (hCount) hCount.textContent = n ? ' (' + n + ')' : '';
    document.querySelectorAll('[data-cd-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = !n;
    });
  }

  function sync() {
    if (window.CartJS && typeof CartJS.getCart === 'function') { try { CartJS.getCart(); } catch (e) {} }
  }

  function refresh(openAfter) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(cart);
        sync();
        if (openAfter) open();
        // Compare-at data needs a network round trip, so the drawer opens immediately
        // with plain prices and repaints once MSRP lands. renderToken guards the
        // repaint: a stale resolve must not overwrite a body render() has since
        // rebuilt for a different cart. Cache is warm after the first open, and the
        // DOMContentLoaded + window.load refreshes usually warm it before any click.
        var tok = renderToken;
        ensureCompare(cart).then(function (changed) {
          if (changed && tok === renderToken) render(cart);
        });
        return cart;
      })
      .catch(function () {});
  }

  /* One delayed re-read after any cart mutation. Redo re-tiers its coverage line
   * (3% of cart across 96 price-tier variants) right AFTER our immediate refresh.
   * The badges are already race-immune (realCount ignores coverage entirely), but the
   * drawer CONTENTS - the coverage row's own price and the estimated total - lag one
   * tier behind until the next read. The timer collapses across rapid mutations so a
   * stepper spree ends with exactly one settling read. */
  var settleT;
  function settle() {
    clearTimeout(settleT);
    settleT = setTimeout(function () { refresh(false); }, 1400);
  }

  function change(line, qty) {
    if (busy) return;
    busy = true; drawer.classList.add('is-busy');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(cart); sync(); settle(); })
      .catch(function () {})
      .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
  }

  document.addEventListener('DOMContentLoaded', function () {
    drawer = document.querySelector('[data-cd]');
    overlay = document.querySelector('[data-cd-ov]');
    if (!drawer || !overlay) return;

    SALE_LABEL = drawer.getAttribute('data-cd-sale-label') || 'Sale';

    // #shopify-section-footer sets position:relative + z-index:2, creating a stacking
    // context in which our z-index is meaningless. Reparent to <body>.
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    body = drawer.querySelector('[data-cd-body]');
    foot = drawer.querySelector('[data-cd-foot]');
    subEl = drawer.querySelector('[data-cd-sub]');
    hCount = drawer.querySelector('[data-cd-hcount]');

    mountBadges();
    refresh(false);

    // Redo adds its coverage line on page load, after our first read, so the coverage
    // row and total could settle one short. Re-read once the page has finished loading
    // so the drawer contents agree.
    window.addEventListener('load', function () { refresh(false); });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

      var submitter = e.submitter || document.activeElement;
      var isBuyNow = !!(submitter && submitter.closest && submitter.closest('[data-crled-buy-now]'));

      e.preventDefault();
      if (busy) return;
      busy = true; drawer.classList.add('is-busy');

      fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function () {
          if (isBuyNow) { window.location.assign('/checkout'); return; }
          return refresh(true).then(function () { settle(); });
        })
        .catch(function () { form.submit(); })
        .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
    }, true);

    document.addEventListener('click', function (e) {
      // 2026-08-10 (Claude): never swallow a click the browser is meant to handle itself.
      // cmd/ctrl/shift/alt-click and any non-primary button now reach the browser, so
      // "open in new tab / new window" works on every link in and around the drawer.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // 2026-08-10 (Claude): the branch that used to stand here was
      //     var link = e.target.closest(CART_LINK);
      //     if (link) { e.preventDefault(); refresh(true); return; }
      // It preventDefault()ed EVERY <a href="*/cart"> on the site, so /cart was
      // unreachable - including from the header. At the time that also orphaned the
      // required attributes[location-type] select that only templates/cart.liquid
      // rendered, so cart links were switched to navigate normally.
      //
      // 2026-08-10 later (Claude): that select is now GONE - removed at Jake's request
      // together with cart.s.min.js's #checkout blocker - so the constraint above no
      // longer applies. Jake chose drawer-over-navigation for the HEADER ICONS ONLY.
      // 2026-08-11: desktop parity - the desktop header cart icon (li.cart-bg > a in
      // snippets/top-site-nav.liquid) now opens the drawer like the mobile icon does.
      // Every other cart link (mobile menu footer, cart page) still navigates, so
      // /cart stays reachable for its lead-time badges and shipping notes. Both icons
      // keep href="/cart" as the no-JS and modifier-click fallback, and the guard
      // above keeps cmd/ctrl-click native.
      var hdrCart = e.target.closest('.mobile-menu__cart, li.cart-bg > a[href$="/cart"]');
      if (hdrCart) { e.preventDefault(); refresh(true); return; }

      if (e.target.closest('[data-cd-close]') || e.target.closest('[data-cd-ov]')) { close(); return; }
      var inc = e.target.closest('[data-cd-inc]');
      var dec = e.target.closest('[data-cd-dec]');
      var rm  = e.target.closest('[data-cd-rm]');
      if (inc || dec || rm) {
        var el = inc || dec || rm;
        var line = parseInt(el.getAttribute('data-cd-inc') || el.getAttribute('data-cd-dec') || el.getAttribute('data-cd-rm'), 10);
        if (rm) return change(line, 0);
        var wrap = el.closest('.crled-cd__qty');
        var qv = wrap ? wrap.querySelector('[data-cd-qv]') : null;
        var cur = qv ? parseInt(qv.textContent, 10) : 1;
        return change(line, inc ? cur + 1 : Math.max(0, cur - 1));
      }
    });

    document.addEventListener('keyup', function (e) { if (e.key === 'Escape') close(); });
  });
})();
